import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert
} from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { colors } from 'styles/colors';
import { GET_SERVICE_PROVIDER } from 'app/graphql/services';
import StudentNavBar from 'app/components/StudentNavBar';
import PaymentResultIcon from './PaymentResultIcon';
import TransactionDetailsCard from './TransactionDetailsCard';
import PaymentResultActions from './PaymentResultActions';
import PaymentResultContent from './PaymentResultContent';
import { PaymentResultProps } from './types';
import { getPaymentMessage } from './paymentResultConfig';
import { clarityEvent } from 'utils/hooks/useClarity';
import { getUserInfo } from 'utils/helpers';

const PaymentResultComponent: React.FC<PaymentResultProps> = ({
  paymentStatus,
  serviceName,
  providerName,
  serviceIdentifier = 'clovemind',
  amount,
  purchaseId,
  transactionId,
  errorMessage,
  retryPath,
  dashboardPath,
  buttonText,
  buttonIcon,
  showNextSteps = false,
  nextStepsContent = [],
  showFAQ = false,
  faqData = [],
  customContent,
  toastMessage,
  loadingTimeout = 5000
}) => {
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [purchaseData, setPurchaseData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const userInfo = getUserInfo();

  // Get URL parameters
  const searchParams = new URLSearchParams(location.search);
  const urlPurchaseId = purchaseId || searchParams.get('purchase_id');
  const urlTransactionId = transactionId || searchParams.get('transaction_id');
  const urlAmount = amount || searchParams.get('amount');
  const urlError = errorMessage || searchParams.get('error');

  // Get service provider details
  const [getServiceProvider, { loading: serviceLoading }] = useLazyQuery(GET_SERVICE_PROVIDER, {
    onCompleted: (data) => {
      if (data?.service_provider) {
        setPurchaseData({
          service_name: data.service_provider.active_pricings?.[0]?.service_name || serviceName || 'One-on-One Counselling',
          provider_name: data.service_provider.name || providerName || 'Clovemind',
          duration_minutes: data.service_provider.active_pricings?.[0]?.duration_minutes || 60
        });
        
        // Show toast notification only once when payment result is processed
        if (toastMessage) {
          const variant = paymentStatus === 'success' ? 'success' : paymentStatus === 'failure' ? 'error' : 'info';
          enqueueSnackbar(toastMessage, { variant });
        }
      }
    },
    onError: (error) => {
      console.error('Service provider fetch error:', error);
      // Still show result page even if we can't fetch service details
      setPurchaseData({
        service_name: serviceName || 'One-on-One Counselling',
        provider_name: providerName || 'Clovemind',
        duration_minutes: 60
      });
      
      if (toastMessage) {
        const variant = paymentStatus === 'success' ? 'success' : paymentStatus === 'failure' ? 'error' : 'info';
        enqueueSnackbar(toastMessage, { variant });
      }
    }
  });

  // Fetch service provider details on mount
  useEffect(() => {
    if (serviceIdentifier) {
      getServiceProvider({
        variables: { identifier: serviceIdentifier }
      });
    } else {
      // If no service identifier, use provided data directly
      setPurchaseData({
        service_name: serviceName || 'Service Purchase',
        provider_name: providerName || 'Service Provider',
        duration_minutes: 60
      });
      setIsLoading(false);
      
      if (toastMessage) {
        const variant = paymentStatus === 'success' ? 'success' : paymentStatus === 'failure' ? 'error' : 'info';
        enqueueSnackbar(toastMessage, { variant });
      }
    }
  }, [serviceIdentifier, getServiceProvider, serviceName, providerName, toastMessage, paymentStatus, enqueueSnackbar]);

  // Control loading state
  useEffect(() => {
    if (!serviceLoading || purchaseData) {
      setIsLoading(false);
    }
  }, [serviceLoading, purchaseData]);

  // Safety timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('Loading timeout - showing content anyway');
      setIsLoading(false);
      if (!purchaseData) {
        setPurchaseData({
          service_name: serviceName || 'Service Purchase',
          provider_name: providerName || 'Service Provider',
          duration_minutes: 60
        });
      }
    }, loadingTimeout);

    return () => clearTimeout(timeout);
  }, [purchaseData, loadingTimeout, serviceName, providerName]);

  // Track payment result page view
  useEffect(() => {
    if (!isLoading && purchaseData && userInfo.id && serviceIdentifier === 'clovemind') {
      const baseMetadata = {
        userId: userInfo.id,
        serviceName: purchaseData.service_name || serviceName || 'Service Purchase'
      };

      switch (paymentStatus) {
        case 'success':
          clarityEvent("selfcare_payment_success_viewed", {
            ...baseMetadata,
            purchaseId: urlPurchaseId || '',
            transactionId: urlTransactionId || '',
            amount: urlAmount || ''
          });
          break;
        case 'failure':
          clarityEvent("selfcare_payment_failure_viewed", {
            ...baseMetadata,
            transactionId: urlTransactionId || '',
            errorMessage: urlError || ''
          });
          break;
        case 'cancelled':
          clarityEvent("selfcare_payment_cancelled_viewed", {
            ...baseMetadata
          });
          break;
      }
    }
  }, [isLoading, purchaseData, paymentStatus, urlPurchaseId, urlTransactionId, urlAmount, urlError, userInfo.id, serviceName, serviceIdentifier]);

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <CircularProgress sx={{ color: colors.primaryBlue, mb: 2 }} />
          <Typography color={colors.fontLight}>
            {paymentStatus === 'success' ? 'Verifying payment...' : 'Loading...'}
          </Typography>
        </Box>
      </Container>
    );
  }

  const finalServiceName = purchaseData?.service_name || serviceName || 'Service Purchase';
  const finalProviderName = purchaseData?.provider_name || providerName || 'Service Provider';
  const displayAmount = urlAmount ? parseFloat(urlAmount).toLocaleString() : null;
  const finalErrorMessage = urlError ? decodeURIComponent(urlError) : errorMessage;

  const messages = getPaymentMessage(paymentStatus);

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        {/* Status Icon */}
        <PaymentResultIcon status={paymentStatus} />

        {/* Status Message */}
        <Typography 
          variant="h3" 
          fontWeight={700} 
          sx={{ 
            color: colors.fontDarkBlue,
            mb: 2,
            fontSize: { xs: '28px', md: '32px' }
          }}
        >
          {messages.title}
        </Typography>

        <Typography 
          variant="h6" 
          sx={{ 
            color: colors.fontLight,
            mb: 1,
            fontSize: '18px'
          }}
        >
          {messages.subtitle}
        </Typography>

        <Typography 
          variant="h5" 
          fontWeight={600} 
          sx={{ 
            color: colors.primaryBlue,
            mb: 4,
            fontSize: '20px'
          }}
          dangerouslySetInnerHTML={{
            __html: finalServiceName
          }}
        />

        {/* Error Alert for failure status */}
        {paymentStatus === 'failure' && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4, 
              textAlign: 'left',
              '& .MuiAlert-message': {
                width: '100%'
              },
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                marginTop: '4px'
              }
            }}
          >
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              {finalErrorMessage || 'Payment failed'}
            </Typography>
            <Typography variant="body2">
              Your payment could not be processed. This could be due to:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li>Insufficient funds in your account</li>
              <li>Bank or card issuer declined the transaction</li>
              <li>Network connectivity issues</li>
              <li>Payment timeout or session expired</li>
              <li>Incorrect payment details</li>
            </Box>
          </Alert>
        )}

        {/* Info Alert for cancelled status */}
        {paymentStatus === 'cancelled' && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 4, 
              textAlign: 'left',
              '& .MuiAlert-message': {
                width: '100%'
              },
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                marginTop: '4px'
              }
            }}
          >
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              No charges applied
            </Typography>
            <Typography variant="body2">
              Your payment was cancelled and no amount has been deducted from your account. You can complete your purchase anytime.
            </Typography>
          </Alert>
        )}

        {/* Transaction Details */}
        <TransactionDetailsCard
          purchaseId={urlPurchaseId}
          transactionId={urlTransactionId}
          amount={displayAmount}
          providerName={finalProviderName}
          status={paymentStatus}
        />

        {/* Action Buttons */}
        <PaymentResultActions
          status={paymentStatus}
          retryPath={retryPath}
          dashboardPath={dashboardPath}
          buttonText={buttonText}
          buttonIcon={buttonIcon}
          serviceIdentifier={serviceIdentifier}
        />
      </Box>

      {/* Additional Content (Next Steps, FAQ, Custom Content) */}
      <PaymentResultContent
        status={paymentStatus}
        showNextSteps={showNextSteps}
        nextStepsContent={nextStepsContent}
        showFAQ={showFAQ}
        faqData={faqData}
        customContent={customContent}
      />
    </Container>
  );
};

const PaymentResult: React.FC<PaymentResultProps> = (props) => {
  return <StudentNavBar mainComponent={<PaymentResultComponent {...props} />} />;
};

export default PaymentResult;